import {
  DiscoveryApi,
  IdentityApi,
  OAuthApi,
} from '@backstage/core-plugin-api';
import { ApiUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';

import {
  AdoMiddlewareOptions,
  ADORequestMiddleware,
  IADORequestMiddleware,
} from '@dewise-devex/backstage-plugin-azdevops-common';
import fetch from 'node-fetch';
import { NotificationApiResponse } from '@dewise-devex/backstage-plugin-notifications-react';

export class ProductClient implements ProductApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly adoMiddleware: IADORequestMiddleware;
  private readonly identityApi: IdentityApi;
  private readonly oAuthApi: OAuthApi;
  private readonly PRODUCT_API_PATH = 'product';

  constructor(
    options: {
      discoveryApi: DiscoveryApi;
      identityApi: IdentityApi;
      authApi: OAuthApi;
    },
    adoApiId: string,
  ) {
    this.identityApi = options.identityApi;
    this.oAuthApi = options.authApi;
    this.adoMiddleware = new ADORequestMiddleware({
      identityApi: this.identityApi,
      authApi: this.oAuthApi,
      adoApiId: adoApiId,
    });
    this.discoveryApi = options.discoveryApi;
  }

  /**
   * Authenticates a request using the IdentityApi.
   * @param url - The URL for the request.
   * @param options - Optional request initialization parameters.
   * @returns The authenticated response.
   */
  private async authenticate(
    url: fetch.RequestInfo,
    options?: fetch.RequestInit,
  ) {
    const authorizedRequest = await ApiUtil.authorizeRequest(this.identityApi);
    return await authorizedRequest.execute(url, options);
  }

  async getProducts() {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/products`;

    const res = (await this.authenticate(url)) as Product[];
    return res.filter(product => product.project);
  }

  async getFilterValuesForWorkItem() {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/filter/work-items`;

    return await this.authenticate(url);
  }

  async getWorkItems(queries: WorkItemQueries) {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/work-items`;
    const queryString = Object.entries(queries)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    return await this.authenticate(`${url}?${queryString}`);
  }

  async getWorkItem(id: string) {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/work-item/${id}`;

    return await this.authenticate(url);
  }

  async createWorkItem(entity: CreateWorkItemParams) {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/create/work-item`;

    const options = {
      method: 'POST',
      body: JSON.stringify(entity as unknown as BodyInit),
      withHeaders: true,
    } as AdoMiddlewareOptions;

    return await this.adoMiddleware.request<any[]>(url, options).then(res => {
      return {
        message: 'Work item created successfully',
        data: res,
      };
    });
  }

  async getTeamMembers(namespaceProductStr: string): Promise<TeamMemberInfo[]> {
    const url = `${await this.discoveryApi.getBaseUrl(this.PRODUCT_API_PATH)}/${namespaceProductStr}/team/members`;

    return await this.authenticate(url);
  }
}

export interface ProductApi {
  createWorkItem: (
    reqParams: CreateWorkItemParams,
  ) => Promise<NotificationApiResponse<WorkItem[]>>;
  getTeamMembers: (namespaceProductStr: string) => Promise<TeamMemberInfo[]>;
  getProducts: () => Promise<Product[]>;
  getFilterValuesForWorkItem: () => Promise<FilterValues>;
  getWorkItems: (queries: WorkItemQueries) => Promise<WorkItemsMeta>;
  getWorkItem: (id: string) => Promise<WorkItem>;
}
